import { createApi } from "@reduxjs/toolkit/query/react";
import type { AxiosRequestConfig, Method } from "axios";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { apiClient, publicClient } from "../../api/client";

export type ApiTag =
  | "Auth"
  | "User"
  | "Notifications"
  | "Bounties"
  | "Trades"
  | "Listings"
  | "Orders"
  | "Disputes"
  | "Conversations"
  | "Messages"
  | "Conventions"
  | "Reviews"
  | "Reports"
  | "Admin"
  | "Meetups"
  | "Drafts"
  | "Moderation"
  | "TradeOffers"
  | "SoldListings"
  | "Cards"
  | "Monetization"
  | "Billing";

export interface AxiosBaseQueryArgs {
  url: string;
  method?: Method;
  data?: unknown;
  params?: AxiosRequestConfig["params"];
  headers?: AxiosRequestConfig["headers"];
  timeout?: number;
  signal?: AbortSignal;
  usePublic?: boolean;
}

export const defaultQueryFreshness = {
  refetchOnMountOrArgChange: 60 as const,
};

const axiosBaseQuery = (): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
  async ({ url, method = "GET", data, params, headers, timeout, signal, usePublic = false }) => {
    const client = usePublic ? publicClient : apiClient;

    try {
      const result = await client.request({
        url,
        method,
        data,
        params,
        headers,
        timeout,
        signal,
      } as AxiosRequestConfig);

      return { data: result.data };
    } catch (axiosError) {
      return {
        error: axiosError,
      };
    }
  };

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQuery(),
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  tagTypes: [
    "Auth",
    "User",
    "Notifications",
    "Bounties",
    "Trades",
    "Listings",
    "Orders",
    "Disputes",
    "Conversations",
    "Messages",
    "Conventions",
    "Reviews",
    "Reports",
    "Admin",
    "Meetups",
    "Drafts",
    "Moderation",
    "TradeOffers",
    "SoldListings",
    "Cards",
    "Monetization",
    "Billing",
  ],
  endpoints: () => ({}),
});
